import React, { useState, useEffect, useContext } from "react";
import Axios from "axios";
import { toast } from "react-toastify";
import { API_ROUTE, Routes } from "../../Helpers/Constants";
import AuthenticationContext from "../../Store/Authentication-Context";

const SEVERITY_OPTIONS = ["Error", "Warning", "Info"];

export default function KnownErrorPatternModal({ show, mode, pattern, onClose, onSaved }) {
    const { setAuthContext } = useContext(AuthenticationContext);

    const emptyForm = {
        errorId: "",
        name: "",
        description: "",
        regexPattern: "",
        regexOptions: "",
        severity: "",
        category: "",
        recommendedFix: "",
        knowledgebaseArticle: "",
        priority: 100,
        isActive: true,
        sampleMessage: "",
    };

    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [inProgress, setInProgress] = useState(false);
    const [regexTestResult, setRegexTestResult] = useState(null);
    const [testInProgress, setTestInProgress] = useState(false);

    useEffect(() => {
        if (!show) return;
        if ((mode === "edit" || mode === "test") && pattern) {
            setForm({
                errorId: pattern.errorId || "",
                name: pattern.name || "",
                description: pattern.description || "",
                regexPattern: pattern.regexPattern || "",
                regexOptions: pattern.regexOptions || "",
                severity: pattern.severity || "",
                category: pattern.category || "",
                recommendedFix: pattern.recommendedFix || "",
                knowledgebaseArticle: pattern.knowledgebaseArticle || "",
                priority: pattern.priority ?? 100,
                isActive: pattern.isActive ?? true,
                sampleMessage: "",
            });
        } else {
            setForm(emptyForm);
        }
        setErrors({});
        setRegexTestResult(null);
    }, [show, mode, pattern]);

    const handleChange = (field) => (e) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
        if (field === "regexPattern" || field === "regexOptions") {
            setRegexTestResult(null);
        }
    };

    function validate() {
        const errs = {};
        if (!form.name.trim()) errs.name = "Required";
        if (!form.description.trim()) errs.description = "Required";
        if (!form.regexPattern.trim()) errs.regexPattern = "Required";
        if (!form.severity.trim()) errs.severity = "Required";
        if (form.priority === "" || form.priority === null || isNaN(Number(form.priority)))
            errs.priority = "Required";
        return errs;
    }

    async function getConfig() {
        const token = await setAuthContext("", new Date());
        return {
            headers: {
                Authorisation: `Bearer ${token}`,
                CloudCoverToken: sessionStorage.getItem("CloudCoverToken"),
            },
        };
    }

    async function handleTestRegex() {
        setTestInProgress(true);
        setRegexTestResult(null);
        try {
            const config = await getConfig();
            const payload = {
                regexPattern: form.regexPattern,
                regexOptions: form.regexOptions,
                sampleMessage: form.sampleMessage,
            };
            const response = await Axios.post(API_ROUTE + Routes.TEST_KNOWN_ERROR_REGEX, payload, config);
            setRegexTestResult(response.data);
        } catch (err) {
            setRegexTestResult({ isValid: false, error: err?.response?.data || "Test failed" });
        } finally {
            setTestInProgress(false);
        }
    }

    async function handleSubmit() {
        if (mode === "test") return;

        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }

        setInProgress(true);
        try {
            const config = await getConfig();
            if (mode === "create") {
                const payload = {
                    errorId: form.errorId.trim() || null,
                    name: form.name,
                    description: form.description,
                    regexPattern: form.regexPattern,
                    regexOptions: form.regexOptions,
                    severity: form.severity,
                    category: form.category,
                    recommendedFix: form.recommendedFix,
                    knowledgebaseArticle: form.knowledgebaseArticle,
                    priority: Number(form.priority),
                    isActive: form.isActive,
                };
                await Axios.post(API_ROUTE + Routes.CREATE_KNOWN_ERROR_PATTERN, payload, config);
                toast.success("Known error pattern created.");
            } else {
                const payload = {
                    name: form.name,
                    description: form.description,
                    regexPattern: form.regexPattern,
                    regexOptions: form.regexOptions,
                    severity: form.severity,
                    category: form.category,
                    recommendedFix: form.recommendedFix,
                    knowledgebaseArticle: form.knowledgebaseArticle,
                    priority: Number(form.priority),
                    isActive: form.isActive,
                };
                await Axios.put(API_ROUTE + Routes.UPDATE_KNOWN_ERROR_PATTERN(pattern.id), payload, config);
                toast.success("Known error pattern updated.");
            }
            onSaved();
        } catch (err) {
            const msg = err?.response?.data || "An error occurred.";
            toast.error(msg);
        } finally {
            setInProgress(false);
        }
    }

    if (!show) return null;

    const isReadOnly = mode === "test";
    const title = mode === "create" ? "Create Known Error Pattern"
        : mode === "edit" ? "Edit Known Error Pattern"
        : "Test Regex Pattern";

    return (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050, overflowY: "auto" }}>
            <div style={{ margin: "40px auto", maxWidth: 720, backgroundColor: "#fff", borderRadius: 6, boxShadow: "0 4px 16px rgba(0,0,0,0.2)" }}>
                <div className="card-header d-flex justify-content-between align-items-center" style={{ padding: "12px 16px", borderBottom: "1px solid #dee2e6" }}>
                    <span className="SectionTitle">{title.toUpperCase()}</span>
                    <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                </div>
                <div style={{ padding: 20 }}>
                    {/* errorId — create mode only */}
                    {mode === "create" && (
                        <div className="mb-3">
                            <label className="form-label">Error ID <span style={{ color: "#6c757d", fontSize: 12 }}>(optional — auto-generated if blank)</span></label>
                            <input type="text" className="form-control" placeholder="e.g. CC365-ERR-0001" value={form.errorId} onChange={handleChange("errorId")} />
                        </div>
                    )}

                    {/* errorId display in edit/test mode */}
                    {(mode === "edit" || mode === "test") && (
                        <div className="mb-3">
                            <label className="form-label">Error ID</label>
                            <input type="text" className="form-control" value={form.errorId} readOnly disabled />
                        </div>
                    )}

                    {!isReadOnly && (
                        <>
                            <div className="mb-3">
                                <label className="form-label">Name <span style={{ color: "red" }}>*</span></label>
                                <input type="text" className={`form-control ${errors.name ? "is-invalid" : ""}`} value={form.name} onChange={handleChange("name")} />
                                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Description <span style={{ color: "red" }}>*</span></label>
                                <textarea className={`form-control ${errors.description ? "is-invalid" : ""}`} rows={3} value={form.description} onChange={handleChange("description")} />
                                {errors.description && <div className="invalid-feedback">{errors.description}</div>}
                            </div>
                        </>
                    )}

                    <div className="mb-3">
                        <label className="form-label">Regex Pattern <span style={{ color: "red" }}>*</span></label>
                        <input type="text" className={`form-control font-monospace ${errors.regexPattern ? "is-invalid" : ""}`} value={form.regexPattern} onChange={handleChange("regexPattern")} readOnly={isReadOnly} disabled={isReadOnly} />
                        {errors.regexPattern && <div className="invalid-feedback">{errors.regexPattern}</div>}
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Regex Options <span style={{ color: "#6c757d", fontSize: 12 }}>(e.g. IgnoreCase)</span></label>
                        <input type="text" className="form-control" placeholder="IgnoreCase" value={form.regexOptions} onChange={handleChange("regexOptions")} readOnly={isReadOnly} disabled={isReadOnly} />
                    </div>

                    {/* Regex Test */}
                    <div className="mb-3">
                        <label className="form-label">Sample Message <span style={{ color: "#6c757d", fontSize: 12 }}>(for regex test only — not saved)</span></label>
                        <div className="input-group">
                            <input type="text" className="form-control" placeholder="Paste a sample log message here" value={form.sampleMessage} onChange={handleChange("sampleMessage")} />
                            <button className="btn btn-outline-secondary" type="button" onClick={handleTestRegex} disabled={testInProgress || !form.regexPattern}>
                                {testInProgress ? "Testing…" : "Test"}
                            </button>
                        </div>
                    </div>

                    {regexTestResult && (
                        <div className="mb-3">
                            {!regexTestResult.isValid ? (
                                <div className="alert alert-danger py-2" style={{ fontSize: 13 }}>
                                    <strong>Invalid regex:</strong> {regexTestResult.error}
                                </div>
                            ) : (
                                <div className={`alert py-2 ${regexTestResult.isMatch ? "alert-success" : "alert-warning"}`} style={{ fontSize: 13 }}>
                                    <strong>{regexTestResult.isMatch ? "Matched" : "No match"}</strong>
                                    {regexTestResult.isMatch && regexTestResult.capturedGroups && Object.keys(regexTestResult.capturedGroups).length > 0 && (
                                        <table className="table table-sm table-bordered mt-2 mb-0">
                                            <thead><tr><th>Group</th><th>Value</th></tr></thead>
                                            <tbody>
                                                {Object.entries(regexTestResult.capturedGroups).map(([k, v]) => (
                                                    <tr key={k}><td><code>{k}</code></td><td>{v}</td></tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {!isReadOnly && (
                        <>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Severity <span style={{ color: "red" }}>*</span></label>
                                    <select className={`form-select ${errors.severity ? "is-invalid" : ""}`} value={form.severity} onChange={handleChange("severity")}>
                                        <option value="">Select…</option>
                                        {SEVERITY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                    {errors.severity && <div className="invalid-feedback">{errors.severity}</div>}
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Category</label>
                                    <input type="text" className="form-control" value={form.category} onChange={handleChange("category")} />
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Recommended Fix</label>
                                <textarea className="form-control" rows={2} value={form.recommendedFix} onChange={handleChange("recommendedFix")} />
                            </div>

                            <div className="mb-3">
                                <label className="form-label">Knowledgebase Article URL</label>
                                <input type="text" className="form-control" value={form.knowledgebaseArticle} onChange={handleChange("knowledgebaseArticle")} />
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Priority <span style={{ color: "red" }}>*</span></label>
                                    <input type="number" className={`form-control ${errors.priority ? "is-invalid" : ""}`} value={form.priority} onChange={handleChange("priority")} />
                                    {errors.priority && <div className="invalid-feedback">{errors.priority}</div>}
                                </div>
                                <div className="col-md-6 mb-3 d-flex align-items-end">
                                    <div className="form-check">
                                        <input type="checkbox" className="form-check-input" id="isActiveCheck" checked={form.isActive} onChange={handleChange("isActive")} />
                                        <label className="form-check-label" htmlFor="isActiveCheck">Active</label>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                <div style={{ padding: "12px 20px", borderTop: "1px solid #dee2e6", display: "flex", justifyContent: "flex-end", gap: 8 }}>
                    <button className="btn btn-secondary" onClick={onClose}>
                        {isReadOnly ? "Close" : "Cancel"}
                    </button>
                    {!isReadOnly && (
                        <button className="btn btn-primary" onClick={handleSubmit} disabled={inProgress}>
                            {inProgress ? "Saving…" : mode === "create" ? "Create" : "Save"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
